import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ShieldCheck, Clock, Video, Users, Crosshair, ArrowRight, AlertTriangle } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';

interface BoostingCalculatorProps {
  initialGame?: string;
}

const RANKS = ['Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Ascendant', 'Immortal', 'Radiant'];
const REGIONS = ['North America (NA)', 'Europe West (EUW)', 'Europe Nordic & East (EUNE)', 'Asia Pacific (AP)', 'Korea (KR)', 'Brazil (BR)'];

export const BoostingCalculator: React.FC<BoostingCalculatorProps> = ({ initialGame = 'valorant' }) => {
  const navigate = useNavigate();
  const { displayPrice } = useCurrency();

  const [serviceType, setServiceType] = useState<'rank_boost' | 'win_boost' | 'placements' | 'coaching'>('rank_boost');
  const [currentRank, setCurrentRank] = useState('Silver');
  const [targetRank, setTargetRank] = useState('Diamond');
  const [region, setRegion] = useState('North America (NA)');
  const [isDuo, setIsDuo] = useState(false);
  const [stream, setStream] = useState(false);
  const [priority, setPriority] = useState(false);
  const [specificAgents, setSpecificAgents] = useState(false);
  const [agentName, setAgentName] = useState('');

  const [priceCents, setPriceCents] = useState(7200); // $72.00
  const [estimatedHours, setEstimatedHours] = useState(15);
  const [calculating, setCalculating] = useState(false);

  // Recalculate with server API
  useEffect(() => {
    let isMounted = true;
    const fetchPrice = async () => {
      setCalculating(true);
      try {
        const res = await fetch('/api/boosting/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentRank, targetRank, isDuo, stream, priority, specificAgents })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted) {
            setPriceCents(data.priceCents);
            setEstimatedHours(data.estimatedHours);
          }
        }
      } catch (err) {
        // Fallback local calc
        const curIdx = RANKS.indexOf(currentRank);
        const tgtIdx = Math.max(curIdx + 1, RANKS.indexOf(targetRank));
        const diff = Math.max(1, tgtIdx - curIdx);
        let base = diff * 1800;
        if (isDuo) base *= 1.3;
        if (stream) base += 800;
        if (priority) base *= 1.25;
        if (specificAgents) base += 500;
        if (isMounted) {
          setPriceCents(Math.round(base));
          setEstimatedHours(diff * 5);
        }
      } finally {
        if (isMounted) setCalculating(false);
      }
    };

    fetchPrice();
    return () => { isMounted = false; };
  }, [currentRank, targetRank, isDuo, stream, priority, specificAgents]);

  const handleProceed = () => {
    // Navigate to checkout with boosting configuration
    navigate('/checkout', {
      state: {
        listing_id: 'lst_val_boost',
        quantity: 1,
        boosting_details: {
          service_type: serviceType,
          current_rank: currentRank,
          target_rank: targetRank,
          server_region: region,
          is_duo: isDuo,
          stream,
          priority,
          specific_agents: specificAgents ? agentName : null
        },
        customPriceCents: priceCents
      }
    });
  };

  const priceInfo = displayPrice(priceCents);

  return (
    <div className="rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder p-6 sm:p-8 shadow-xl">
      
      {/* Service Type Tabs */}
      <div className="flex flex-wrap gap-2 pb-6 border-b border-brand-lightBorder dark:border-brand-darkBorder">
        {[
          { id: 'rank_boost', label: 'Rank Boost' },
          { id: 'win_boost', label: 'Net Wins' },
          { id: 'placements', label: 'Placement Matches' },
          { id: 'coaching', label: '1-on-1 Coaching' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setServiceType(tab.id as any)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              serviceType === tab.id
                ? 'bg-brand-blue text-white shadow-glow-blue'
                : 'bg-brand-lightSec dark:bg-brand-darkElevated text-brand-lightSecText dark:text-brand-darkSecText hover:text-brand-lightText dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
        
        {/* Left Column: Selectors */}
        <div className="space-y-6">
          {/* Rank Selectors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
                Current Rank
              </label>
              <select
                value={currentRank}
                onChange={(e) => setCurrentRank(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-sm font-semibold text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
              >
                {RANKS.slice(0, -1).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
                Desired Rank
              </label>
              <select
                value={targetRank}
                onChange={(e) => setTargetRank(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-sm font-semibold text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
              >
                {RANKS.slice(RANKS.indexOf(currentRank) + 1).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Region Selector */}
          <div>
            <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
              Server / Region
            </label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-sm font-semibold text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              {REGIONS.map((reg) => (
                <option key={reg} value={reg}>{reg}</option>
              ))}
            </select>
          </div>

          {/* Extra Options Checkboxes */}
          <div className="space-y-3 pt-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-brand-lightSecText dark:text-brand-darkSecText">
              Customization Options
            </h4>

            {/* Duo Queue */}
            <label className="flex items-center justify-between p-3 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated cursor-pointer">
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-brand-blue dark:text-brand-accent" />
                <div>
                  <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">Duo Queue (Play Together)</p>
                  <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">No account sharing required</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-brand-blue dark:text-brand-accent">+30%</span>
                <input
                  type="checkbox"
                  checked={isDuo}
                  onChange={(e) => setIsDuo(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue"
                />
              </div>
            </label>

            {/* Live Streaming */}
            <label className="flex items-center justify-between p-3 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated cursor-pointer">
              <div className="flex items-center gap-3">
                <Video className="w-4 h-4 text-purple-500" />
                <div>
                  <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">Private Live Stream</p>
                  <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">Watch booster play in real-time</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-purple-500">+$8.00</span>
                <input
                  type="checkbox"
                  checked={stream}
                  onChange={(e) => setStream(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue"
                />
              </div>
            </label>

            {/* Express Priority */}
            <label className="flex items-center justify-between p-3 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated cursor-pointer">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">VIP Express Priority</p>
                  <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">Started within 15 minutes</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-amber-500">+25%</span>
                <input
                  type="checkbox"
                  checked={priority}
                  onChange={(e) => setPriority(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue"
                />
              </div>
            </label>

            {/* Specific Agents */}
            <label className="flex items-center justify-between p-3 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated cursor-pointer">
              <div className="flex items-center gap-3">
                <Crosshair className="w-4 h-4 text-emerald-500" />
                <div>
                  <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">Specific Agents / Champions</p>
                  <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">Booster plays only requested pool</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-emerald-500">+$5.00</span>
                <input
                  type="checkbox"
                  checked={specificAgents}
                  onChange={(e) => setSpecificAgents(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue"
                />
              </div>
            </label>

            {specificAgents && (
              <input
                type="text"
                placeholder="e.g. Jett, Reyna, Omen"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText"
              />
            )}
          </div>
        </div>

        {/* Right Column: Order Summary & Checkout Action */}
        <div className="flex flex-col justify-between p-6 rounded-2xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder">
          <div>
            <h3 className="font-extrabold text-base text-brand-lightText dark:text-brand-darkText mb-4">
              Boost Order Summary
            </h3>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-1 border-b border-brand-lightBorder/50 dark:border-brand-darkBorder/50">
                <span className="text-brand-lightSecText dark:text-brand-darkSecText">Path:</span>
                <span className="font-bold text-brand-lightText dark:text-brand-darkText">{currentRank} ➔ {targetRank}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-brand-lightBorder/50 dark:border-brand-darkBorder/50">
                <span className="text-brand-lightSecText dark:text-brand-darkSecText">Region:</span>
                <span className="font-semibold text-brand-lightText dark:text-brand-darkText">{region}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-brand-lightBorder/50 dark:border-brand-darkBorder/50">
                <span className="text-brand-lightSecText dark:text-brand-darkSecText">Queue Mode:</span>
                <span className="font-semibold text-brand-lightText dark:text-brand-darkText">{isDuo ? 'Duo Queue (Self-Play)' : 'Solo Pilot (VPN Matched)'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-brand-lightBorder/50 dark:border-brand-darkBorder/50">
                <span className="text-brand-lightSecText dark:text-brand-darkSecText">Est. Completion:</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> ~{estimatedHours} hours
                </span>
              </div>
            </div>

            {/* Service & Publisher Disclaimer */}
            <div className="mt-6 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Publisher Notice:</strong> Boosting services are subject to publisher terms of service. For maximum safety, Duo Queue requires no credential sharing.
              </span>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-lightBorder dark:border-brand-darkBorder">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <span className="text-xs text-brand-lightSecText dark:text-brand-darkSecText block">Total Calculated Price</span>
                <span className="text-[11px] text-brand-lightSecText">{priceInfo.chargedUsd}</span>
              </div>
              <span className="font-black text-3xl text-brand-blue dark:text-brand-accent">
                {priceInfo.formatted}
              </span>
            </div>

            <button
              onClick={handleProceed}
              disabled={calculating}
              className="w-full py-3.5 px-6 rounded-2xl font-extrabold text-sm text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              <span>{calculating ? 'Calculating...' : 'Configure & Checkout'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
